"use client";

import React, { useEffect, useState } from "react";
import CarCompetitionForDesktop from "./carCompetitionForDesktop";
import CarCompetitionForMobile from "./carCompetitionForMobile";
import { getPrizeMediaCarousel, getSuperCarPrizeDetails } from "./car-service";
import { CompetitionState, CarContextDetails, PrizeMediaCarouselResponse, SuperCarPrizeDetails } from "./types";
import { CarCompetitionData } from "./contextProvider";
import "react-loading-skeleton/dist/skeleton.css";
import Modal from "../../../components/Modal/modal";
import { useAppDispatch, useAppSelector } from "@/app/redux/hooks";
import { publishModalVisibility } from "@/app/redux/features/modal/modalSlice";
import { ToastProvider } from "@/app/components/ToastNotification/toastProvider";
import BotbPassDrawer from "@/app/(general-header)/botb-pass-drawer/BotbPassDrawer";
import CarCustomHeader from "@/app/(custom-header)/header/carCustomHeader";
import DeviceProvider from "@/app/redux/features/device/DeviceProvider";
import { useIsMobile } from "../../../redux/features/device/selectors";
import useXcCompetition from "./useXcCompetition";
import dayjs from "dayjs";
import { resolveCompetitionEndDate } from "./mocks/competitionEndDate";

const TWO_WEEKS = 1000 * 60 * 60 * 24 * 7 * 2;
const ONE_WEEK = 1000 * 60 * 60 * 24 * 7;
const THREE_DAYS = 1000 * 60 * 60 * 24 * 3;
const TWO_DAYS = 1000 * 60 * 60 * 24 * 2;
const TODAY = 1000 * 60 * 60 * 24;

const currencyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const deriveCompetitionState = (endDate: string): CompetitionState => {
  const endDateInMs = dayjs(endDate).diff(dayjs(), "milliseconds");
  if (endDateInMs > TWO_WEEKS) return "over14Weeks";
  if (endDateInMs > ONE_WEEK && endDateInMs <= TWO_WEEKS) return "nextWeek";
  if (endDateInMs <= ONE_WEEK && endDateInMs > THREE_DAYS) return "thisWeek";
  if (endDateInMs < THREE_DAYS && endDateInMs > TWO_DAYS) return "last3Days";
  if (endDateInMs < TWO_DAYS && endDateInMs > TODAY) return "last2Days";
  if (endDateInMs <= TODAY) return "today";
  return "over14Weeks";
};

const resolveMediaUrl = (url: string) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.NEXT_PUBLIC_HOST}${url}`;
};

const getReferencePrizeIdFromDom = () => {
  if (typeof document === "undefined") return 0;
  const raw = document.getElementById("reference-prize-id")?.getAttribute("value");
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const CarCompetition = () => {
  const carCompetition = useXcCompetition();
  const isMobile = useIsMobile();
  const [referencePrizeId] = useState(() => getReferencePrizeIdFromDom());
  const [contextData, setContextData] = useState<CarContextDetails>({
    competitionId: 0,
    prizeId: 0,
    prizeTitle: "",
    prizeSubtitle: "",
    cashAlternativeText: "",
    hideCashAlternative: false,
    carValue: "",
    carValueNumber: 0,
    description: "",
    detailsHtml: "",
    specs: [],
    media: [],
    heroImage: "",
    specsImage: "",
    galleryImages: [],
    isPrizeAvailable: false,
    shouldShowSubscriptionDrawer: false,
    competitionState: "over14Weeks",
    currencySymbol: "£",
    ticketPriceLabel: "",
    countdownText: "",
    competitionEndDate: "",
    isVideo: false,
    secondarySectionHtml: "",
    secondarySectionCtaText: "",
    secondarySectionPopupHtml: "",
    setContextData: () => {},
  });
  const [isPrizeDataReady, setIsPrizeDataReady] = useState(false);
  const isModalOpen = useAppSelector(state => state.modal.isOpen);
  const modalData = useAppSelector(state => state.modal.data);
  const modalOverlay = useAppSelector(state => state.modal.withOverlay);
  const modalClassNames = useAppSelector(state => state.modal.modalClassNames);
  const modalStyles = useAppSelector(state => state.modal.modalStyles);
  const dispatch = useAppDispatch();

  const prizeId = referencePrizeId || carCompetition.FeaturedPrizeId;

  useEffect(() => {
    if (prizeId) {
      loadPrizeData(prizeId);
    }
  }, [prizeId, carCompetition.CompetitionId]);

  const loadPrizeData = async (resolvedPrizeId: number) => {
    setIsPrizeDataReady(false);
    try {
      const detailsRes = await getSuperCarPrizeDetails(resolvedPrizeId);
      const details = (await detailsRes.json()) as SuperCarPrizeDetails;
      const endDate = resolveCompetitionEndDate(
        details.CompetitionEndDate || carCompetition.EndDate || carCompetition.EndDateTime
      );
      const retail = details.RetailPriceGbp ?? 0;

      let media: string[] = [];
      try {
        const mediaRes = await getPrizeMediaCarousel(resolvedPrizeId);
        const mediaJson = (await mediaRes.json()) as PrizeMediaCarouselResponse;
        media = (mediaJson?.Data?.Media?.filter(Boolean) ?? []).map(resolveMediaUrl);
      } catch (mediaErr) {
        console.error(`Error while retrieving XC gallery media - ${mediaErr}`);
      }

      setContextData(prev => ({
        ...prev,
        competitionId: details.CompetitionId || carCompetition.CompetitionId,
        prizeId: resolvedPrizeId,
        prizeTitle: details.PrizeTitle || carCompetition.FeaturedPrizeModel || carCompetition.Title || "",
        prizeSubtitle: carCompetition.SubtitleText || "",
        cashAlternativeText: details.CashAlternativeText || carCompetition.AdditionalCashText || "",
        hideCashAlternative: !details.CashAlternativeText,
        carValue: retail ? currencyFormatter.format(retail) : "",
        carValueNumber: retail,
        description: "",
        detailsHtml: details.SecondarySectionPopupText || "",
        specs: [],
        media,
        heroImage: resolveMediaUrl(details.MainMediaUrl) || carCompetition.FeaturedPrizeThumbnail || "",
        specsImage: resolveMediaUrl(details.SecondaryImageUrl) || resolveMediaUrl(details.MainMediaUrl),
        galleryImages: media,
        isPrizeAvailable: Boolean(resolvedPrizeId),
        shouldShowSubscriptionDrawer: false,
        competitionState: deriveCompetitionState(endDate),
        currencySymbol: carCompetition.CurrencySymbol || "£",
        ticketPriceLabel: carCompetition.FormattedPrice || "",
        countdownText: details.CountdownText || "",
        competitionEndDate: endDate,
        isVideo: Boolean(details.IsVideo),
        secondarySectionHtml: details.SecondarySectionText || "",
        secondarySectionCtaText: details.SecondarySectionCTAText || "SHOW ALL SPECIFICATIONS",
        secondarySectionPopupHtml: details.SecondarySectionPopupText || "",
      }));
    } catch (err) {
      console.error(`Error while retrieving XC prize data - ${err}`);
      const endDate = resolveCompetitionEndDate(carCompetition.EndDate || carCompetition.EndDateTime);
      setContextData(prev => ({
        ...prev,
        competitionId: carCompetition.CompetitionId,
        prizeId: resolvedPrizeId,
        prizeTitle: carCompetition.FeaturedPrizeModel || carCompetition.Title || "",
        prizeSubtitle: carCompetition.SubtitleText || "",
        cashAlternativeText: carCompetition.AdditionalCashText || "",
        heroImage: carCompetition.FeaturedPrizeThumbnail || "",
        specsImage: carCompetition.FeaturedPrizeThumbnail || "",
        isPrizeAvailable: Boolean(resolvedPrizeId),
        competitionState: deriveCompetitionState(endDate),
        currencySymbol: carCompetition.CurrencySymbol || "£",
        ticketPriceLabel: carCompetition.FormattedPrice || "",
        competitionEndDate: endDate,
      }));
    } finally {
      setIsPrizeDataReady(true);
    }
  };

  return (
    <DeviceProvider>
      <CarCompetitionData.Provider value={{ ...contextData, setContextData }}>
        <CarCustomHeader />
        <article className="block">
          {!isPrizeDataReady ? (
            <div className="w-full aspect-[1/1] bg-[#f1f1f1] min-h-[calc(100vh-56px)] lg:min-h-[calc(100vh-60px)] overflow-hidden" />
          ) : !isMobile ? (
            <CarCompetitionForDesktop />
          ) : (
            <CarCompetitionForMobile />
          )}
        </article>
        {contextData && <BotbPassDrawer shouldShowSubscriptionDrawer={contextData.shouldShowSubscriptionDrawer} />}
        <Modal
          withOverlay={modalOverlay}
          closeModal={event => {
            event.stopPropagation();
            dispatch(publishModalVisibility({ isOpen: false }));
          }}
          isOpen={isModalOpen}
          body={modalData}
          title=""
          modalClassNames={modalClassNames}
          customStyles={modalStyles}
        />
        <ToastProvider />
      </CarCompetitionData.Provider>
    </DeviceProvider>
  );
};

export default CarCompetition;
